/**
 * Company-profile schema: the shared technical-baseline sections plus the map
 * of AI-intake fields the company profile owns.
 *
 * Backend has the parallel skeleton in `backend/app/domain/company_schema.py`
 * (`BASELINE_SECTIONS` + `COMPANY_OWNED_FIELDS`). Tests assert the two stay in
 * sync (`backend/tests/test_schema_in_sync.py`).
 *
 * The baseline sections store on the company profile (`target: 'company'`) and
 * are rendered inside both the WAFR and FTR questionnaires so neither module
 * re-asks what the other captured.
 */

import { SECTIONS as INTAKE_SECTIONS } from './intakeSchema'
import type { ChoiceOption, SectionDef } from './types'

/** Options whose stored value is also the label. */
const choices = (...values: string[]): ChoiceOption[] => values.map((v) => ({ value: v, label: v }))

const HOSTING_MODEL_OPTIONS = choices(
  'Single AWS account', 'Multi-account (Organizations)', 'Hybrid (AWS + on-prem)', 'Multi-cloud',
)

const ENVIRONMENT_OPTIONS = choices('Production', 'Staging', 'QA / Test', 'Development', 'Sandbox', 'DR')

const IAC_USAGE_OPTIONS = choices(
  'Everything in IaC', 'Most resources', 'Some resources', 'None — console-managed',
)

const MFA_ENFORCEMENT_OPTIONS = choices('Enforced for all users', 'Admins only', 'Partial', 'Not enforced')

const ENCRYPTION_AT_REST_OPTIONS = choices(
  'Everywhere (KMS-managed)', 'Most data stores', 'Some data stores', 'None / unknown',
)

const ENCRYPTION_IN_TRANSIT_OPTIONS: ChoiceOption[] = [
  { value: 'TLS everywhere', label: 'TLS everywhere (incl. internal traffic)' },
  { value: 'External traffic only', label: 'External traffic only' },
  { value: 'Partial', label: 'Partial' },
  { value: 'None / unknown', label: 'None / unknown' },
]

const SECURITY_TOOLING_OPTIONS = choices(
  'GuardDuty', 'Security Hub', 'AWS Config', 'Inspector', 'Macie', 'WAF',
  'Shield Advanced', 'Third-party SIEM', 'CSPM (Wiz / Orca / Prisma…)',
)

const ON_CALL_OPTIONS = choices(
  'Dedicated 24/7 rotation', 'Business-hours rotation', 'Best effort / ad hoc', 'None',
)

const DEPLOYMENT_FREQUENCY_OPTIONS = choices('Multiple times a day', 'Weekly', 'Monthly', 'Ad hoc')

const DOCUMENTATION_OPTIONS = choices(
  'Current and comprehensive', 'Mostly current', 'Outdated', 'Minimal / none',
)

/** The company-basics section definition (first in the intake schema, phase
 *  'company' — rendered only through CompanyProfileForm, never as an intake
 *  section). */
export const COMPANY_PROFILE_SECTION: SectionDef = INTAKE_SECTIONS.find(
  (s) => s.key === 'company_profile',
)!

/** Shared technical-baseline sections — stored on the company profile, rendered
 *  inside the WAFR and FTR questionnaires. Mirrors
 *  `company_schema.BASELINE_SECTIONS` (sync-tested). */
export const BASELINE_SECTIONS: SectionDef[] = [
  {
    key: 'architecture_baseline',
    number: 1,
    target: 'company',
    title: 'Architecture Baseline',
    navTitle: 'Architecture',
    description:
      'How the AWS estate is laid out: account structure, core workloads, networking, environments, and infrastructure-as-code. Shared between WAFR and FTR so it is captured once.',
    help:
      "This technical-baseline section describes the shape of the AWS estate — account structure, core workloads, networking, environments, and infrastructure-as-code. It's the context every other technical control is judged against.\n\nIt's **captured once on the company profile and shared by both the WAFR and FTR reviews** (and shown on the profile), so neither module re-asks it. Fill it in as it actually is today, not as the target-state architecture.",
    fields: [
      {
        key: 'hosting_model',
        label: 'Hosting model',
        hint:
          'The overall shape of the estate — a single AWS account, a multi-account Organizations setup, hybrid with on-prem, or spread across multiple clouds.',
        help:
          "**Why it matters:** the account/estate shape is the foundation both reviews reason about — a single account, a multi-account Organizations setup, hybrid, or multi-cloud each imply very different security, blast-radius, and operations expectations. FTR's eligibility and WAFR's Security and Operations pillars all start here.\n\n**A good answer:** the honest overall shape today, not the target-state aspiration.\n\n**Example:** Multi-account via AWS Organizations — separate management, log-archive, security, and per-environment workload accounts, all governed by Control Tower.",
        type: { kind: 'radio', options: HOSTING_MODEL_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'core_workloads',
        label: 'Core workloads',
        hint:
          'The main applications or systems running on AWS — one per line (e.g. "Customer-facing e-commerce API", "Nightly ETL into Redshift").',
        help:
          "**Why it matters:** both reviews scope their findings to the workloads that matter, and the AI proposals ground on what's actually running. Naming the main systems keeps recommendations landing on real applications rather than in the abstract.\n\n**A good answer:** one line per significant system, each with a hint of its role and rough footprint.\n\n**Example:**\n- Customer-facing e-commerce API (ECS + RDS)\n- Nightly ETL into Redshift\n- Internal admin portal (Lambda + DynamoDB)",
        type: { kind: 'list', placeholder: 'One workload per line' },
        priority: 'recommended',
      },
      {
        key: 'network_topology',
        label: 'Network topology',
        hint:
          'How VPCs, subnets, and connectivity are arranged — hub-and-spoke with Transit Gateway, single VPC, Direct Connect/VPN to on-prem, PrivateLink usage, etc.',
        help:
          "**Why it matters:** network layout drives security and resilience conclusions in both reviews — how VPCs, subnets, and connectivity are arranged determines blast radius, data-exfiltration paths, and whether a workload can be multi-AZ or reach on-prem.\n\n**A good answer:** a couple of sentences on VPC structure, cross-account and on-prem connectivity, and how public exposure is handled.\n\n**Example:** Hub-and-spoke via Transit Gateway; workload VPCs are private-subnet-only with egress through a central inspection VPC; Direct Connect to the Chicago datacenter; public traffic terminates at CloudFront + WAF.",
        type: { kind: 'textarea', rows: 2, placeholder: 'Hub-and-spoke via Transit Gateway, Direct Connect to on-prem…' },
        priority: 'recommended',
      },
      {
        key: 'environments',
        label: 'Environments',
        hint:
          'Which separated environments exist today. Separate production from non-production accounts is a baseline AWS recommendation.',
        help:
          "**Why it matters:** separating production from non-production is a baseline AWS recommendation both reviews expect — a shared prod/dev account means a test mistake can take down customers. The set of environments also tells the reviewer how change flows toward production.\n\n**A good answer:** tick every separated environment that genuinely exists (ideally in its own account or VPC), not just names on paper.\n\n**Example:** Production, Staging, and Development each in a dedicated account; a shared Sandbox for experiments; a DR region stood up for the two tier-1 services.",
        type: { kind: 'multiselect', options: ENVIRONMENT_OPTIONS, allowOther: true },
        priority: 'recommended',
      },
      {
        key: 'iac_usage',
        label: 'Infrastructure-as-code coverage',
        hint:
          'How much of the estate is defined in IaC (Terraform, CDK, CloudFormation…) versus hand-built in the console. Drives repeatability and drift risk.',
        help:
          "**Why it matters:** how much of the estate is defined in infrastructure-as-code drives repeatability and drift risk — a console-built estate can't be reliably reproduced, reviewed, or recovered, which both WAFR and FTR treat as an operations weakness.\n\n**A good answer:** the honest coverage level and which tool is used.\n\n**Example:** Most resources in Terraform (networking, IAM, ECS, RDS); a handful of legacy S3 buckets and IAM roles are still console-managed and being imported this quarter.",
        type: { kind: 'radio', options: IAC_USAGE_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'notes',
        label: 'Architecture notes',
        hint:
          'Anything else about the architecture that the structured fields above miss — upcoming re-platforming, known debt, unusual constraints.',
        type: { kind: 'textarea', rows: 3 },
      },
    ],
  },
  {
    key: 'security_posture',
    number: 2,
    target: 'company',
    title: 'Security Posture',
    navTitle: 'Security',
    description:
      'The company-wide security baseline: identity, MFA, encryption, tooling, and vulnerability management. Shared between WAFR and FTR so it is captured once.',
    help:
      "This technical-baseline section is the company-wide security baseline — identity, MFA, encryption at rest and in transit, detection tooling, and vulnerability management. These are the fundamentals FTR's Security review and WAFR's Security pillar both grade.\n\nIt's **captured once on the company profile and shared by both the WAFR and FTR reviews** (and shown on the profile), so neither module re-asks it.",
    fields: [
      {
        key: 'identity_provider',
        label: 'Identity provider',
        hint:
          'How humans authenticate to AWS and internal systems — IAM Identity Center, Okta, Microsoft Entra ID, Cognito, plain IAM users, etc.',
        help:
          "**Why it matters:** how humans authenticate is the root of the whole access model both reviews examine. A central IdP (IAM Identity Center, Okta, Entra) with federation is far stronger than scattered IAM users, and it's the anchor for the MFA and least-privilege findings.\n\n**A good answer:** the actual identity provider(s) and how they connect to AWS.\n\n**Example:** Okta as the IdP, federated into AWS IAM Identity Center for all human access; no standalone IAM users carrying console passwords.",
        type: { kind: 'text', placeholder: 'IAM Identity Center + Okta' },
        priority: 'recommended',
      },
      {
        key: 'mfa_enforcement',
        label: 'MFA enforcement',
        hint:
          'How broadly multi-factor authentication is enforced across human access. "Admins only" and "Partial" are common findings both reviews flag.',
        help:
          "**Why it matters:** WAFR's Security pillar leans on this heavily — a password alone must not grant access — and a high risk there fails the FTR's WAFR pathway. \"Admins only\" and \"Partial\" are exactly the gaps a review flags as findings.\n\n**A good answer:** \"Enforced for all users\" — enforced, not merely available — is the passing answer.\n\n**Example:** Enforced for all users via an Okta conditional-access policy requiring MFA on every AWS and SaaS sign-in; break-glass accounts use hardware security keys.",
        type: { kind: 'radio', options: MFA_ENFORCEMENT_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'encryption_at_rest',
        label: 'Encryption at rest',
        hint:
          'How consistently data stores (S3, EBS, RDS, DynamoDB…) are encrypted at rest, and whether keys are KMS-managed.',
        help:
          "**Why it matters:** both reviews expect data at rest encrypted, ideally with KMS-managed keys, so a snapshot, disk, or bucket that leaks or is stolen is useless without the key. \"Some data stores\" is a common finding, because the gaps are often exactly where sensitive data sits.\n\n**A good answer:** \"Everywhere (KMS-managed)\" — S3, EBS, RDS, DynamoDB, and backups all encrypted with managed keys.\n\n**Example:** KMS encryption enforced on all S3 buckets (SSE-KMS), EBS volumes, RDS, and DynamoDB via SCPs and Config rules; no unencrypted stores in production.",
        type: { kind: 'radio', options: ENCRYPTION_AT_REST_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'encryption_in_transit',
        label: 'Encryption in transit',
        hint:
          'Whether traffic is TLS-protected end to end — including service-to-service traffic inside the VPC, not just the public edge.',
        help:
          "**Why it matters:** reviewers want traffic TLS-protected end to end — including service-to-service traffic inside the VPC, not just the public edge — so an attacker on the network can't read data in flight. \"External traffic only\" leaves internal east-west traffic exposed to lateral movement.\n\n**A good answer:** \"TLS everywhere,\" including internal/east-west traffic.\n\n**Example:** TLS 1.2+ terminates at the ALB and is re-encrypted to targets; the internal service mesh uses mTLS; RDS and Redshift require SSL connections.",
        type: { kind: 'radio', options: ENCRYPTION_IN_TRANSIT_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'security_tooling',
        label: 'Security tooling in use',
        hint:
          'Detection and posture-management tooling that is actually enabled today. The "Other" box accepts comma-separated extras.',
        help:
          "**Why it matters:** detection and posture-management tooling is how the company would actually notice a threat or misconfiguration. Both reviews look for the AWS-native detection stack (GuardDuty, Security Hub, Config) to be **enabled**, not just licensed — findings need somewhere to surface.\n\n**A good answer:** tick what's genuinely enabled today; use the \"Other\" box for a third-party SIEM or CSPM.\n\n**Example:** GuardDuty and Security Hub enabled org-wide, AWS Config with conformance packs, Inspector scanning ECR images; findings forwarded to a Splunk SIEM.",
        type: { kind: 'multiselect', options: SECURITY_TOOLING_OPTIONS, allowOther: true },
        priority: 'recommended',
      },
      {
        key: 'vulnerability_mgmt',
        label: 'Vulnerability management',
        hint:
          'How vulnerabilities are found and fixed — scanning cadence, patching SLAs, who owns remediation (e.g. "Inspector weekly, criticals patched within 7 days").',
        help:
          "**Why it matters:** both reviews care not just whether vulnerabilities are found but that they're **fixed** on a defined SLA with a clear owner — scanning without remediation is theatre. This is where FTR and WAFR probe the operational side of security.\n\n**A good answer:** scanning cadence, patch SLAs by severity, and who owns remediation.\n\n**Example:** Inspector scans images and hosts continuously; criticals patched within 7 days, highs within 30, tracked in Jira with the platform team as owner and a monthly patch report to the CISO.",
        type: { kind: 'textarea', rows: 2, placeholder: 'Inspector weekly scans, 7-day SLA on criticals…' },
        priority: 'recommended',
      },
      {
        key: 'notes',
        label: 'Security notes',
        hint:
          'Anything else about the security posture — recent audits or incidents, compensating controls, planned improvements.',
        type: { kind: 'textarea', rows: 3 },
      },
    ],
  },
  {
    key: 'operations_practices',
    number: 3,
    target: 'company',
    title: 'Operations Practices',
    navTitle: 'Operations',
    description:
      'How the company runs what it builds: monitoring, on-call, deployment cadence, incidents, backups, and documentation. Shared between WAFR and FTR so it is captured once.',
    help:
      "This technical-baseline section describes how the company runs what it builds — monitoring, on-call, deployment cadence, incident handling, backups, and documentation. These are the operational-readiness signals FTR's Operations review and WAFR's Operational Excellence pillar both grade.\n\nIt's **captured once on the company profile and shared by both the WAFR and FTR reviews** (and shown on the profile), so neither module re-asks it.",
    fields: [
      {
        key: 'monitoring_stack',
        label: 'Monitoring stack',
        hint:
          'The observability tooling in production today — CloudWatch, Datadog, Grafana, New Relic, OpenTelemetry — and roughly what it covers.',
        help:
          "**Why it matters:** observability is the precondition for operating anything reliably, and both reviews want to know what's watched and how. A long tool list means little if it doesn't cover the critical paths, so describe coverage, not just product names.\n\n**A good answer:** the tools in production plus roughly what they cover — metrics, logs, traces, synthetics.\n\n**Example:** CloudWatch for infra metrics and logs, Datadog APM for traces across the ECS services, synthetic checks on the top five customer journeys; dashboards per service with SLO burn alerts.",
        type: { kind: 'textarea', rows: 2, placeholder: 'CloudWatch + Datadog APM…' },
        priority: 'recommended',
      },
      {
        key: 'on_call_model',
        label: 'On-call model',
        hint:
          'Who answers when production breaks — a staffed 24/7 rotation, business-hours coverage, or whoever notices first.',
        help:
          "**Why it matters:** when production breaks, who answers determines how long an outage lasts. Both reviews weigh this heavily — \"best effort / ad hoc\" coverage is a resilience and operations finding, especially for customer-facing workloads.\n\n**A good answer:** the honest coverage model for production incidents.\n\n**Example:** Dedicated 24/7 rotation across two follow-the-sun teams via PagerDuty — primary plus secondary on-call, a 15-minute ack SLA, and documented escalation to engineering leadership.",
        type: { kind: 'radio', options: ON_CALL_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'deployment_frequency',
        label: 'Deployment frequency',
        hint:
          'How often changes ship to production. A rough proxy for delivery automation maturity — daily shippers almost always have CI/CD and rollback in place.',
        help:
          "**Why it matters:** deployment cadence is a reliable proxy for delivery-automation maturity — teams shipping daily almost always have CI/CD, automated tests, and fast rollback, while ad-hoc deploys usually mean manual, risky change. Both reviews read it as a maturity signal.\n\n**A good answer:** the real cadence for production, not the theoretical capability.\n\n**Example:** Multiple times a day — every merged PR auto-deploys through CI/CD with a canary and one-click rollback; roughly 20 production deploys a day across services.",
        type: { kind: 'radio', options: DEPLOYMENT_FREQUENCY_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'incident_process',
        label: 'Incident process',
        hint:
          'How incidents are declared, escalated, and reviewed — paging tool, severity levels, and whether post-incident reviews actually happen.',
        help:
          "**Why it matters:** a defined incident process — declaration, severity levels, escalation, and after-the-fact review — is what turns an outage into learning instead of a repeat. Both reviews look for post-incident reviews that actually happen and drive fixes.\n\n**A good answer:** the paging tool, severity scheme, and whether blameless post-mortems are run and tracked to closure.\n\n**Example:** PagerDuty with sev1–3 levels; sev1/2 spin up an incident channel and a commander; blameless post-mortems within 5 days with action items tracked to closure in Jira.",
        type: { kind: 'textarea', rows: 2, placeholder: 'PagerDuty, sev1–3 levels, blameless post-mortems…' },
        priority: 'recommended',
      },
      {
        key: 'backup_practices',
        label: 'Backup practices',
        hint:
          'What gets backed up, on what schedule, and whether restores are ever tested — an untested backup is a hope, not a practice.',
        help:
          "**Why it matters:** backups underpin WAFR's Reliability pillar, and Reliability is one of the three pillars that must carry zero high risks for a WAFR to pass the FTR. What matters is coverage, retention, and whether restores are actually tested — an untested backup is a hope, not a practice.\n\n**A good answer:** what's backed up, on what schedule and retention, and the restore-test cadence.\n\n**Example:** AWS Backup daily with 35-day retention across RDS, DynamoDB, and EFS, cross-region copies for tier-1 data; quarterly restore drills into a sandbox, the last drill documented.",
        type: { kind: 'textarea', rows: 2, placeholder: 'AWS Backup daily, 35-day retention, quarterly restore tests…' },
        priority: 'recommended',
      },
      {
        key: 'documentation_state',
        label: 'Documentation state',
        hint:
          'How current the runbooks and architecture docs are. Both reviews lean on documentation quality when scoring operational readiness.',
        help:
          "**Why it matters:** both reviews lean on documentation quality when scoring operational readiness — current runbooks and architecture docs are what let a team operate, onboard, and recover without the original author. \"Outdated\" or \"minimal\" docs are a recurring finding.\n\n**A good answer:** an honest read on how current and complete the runbooks and architecture docs are.\n\n**Example:** Mostly current — architecture diagrams and runbooks live in the wiki and are reviewed each quarter; a few older services still have stale docs already flagged for update.",
        type: { kind: 'radio', options: DOCUMENTATION_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'notes',
        label: 'Operations notes',
        hint:
          'Anything else about how the estate is operated — team structure, managed-service providers, known operational pain.',
        type: { kind: 'textarea', rows: 3 },
      },
    ],
  },
]

/** AI-intake fields owned by the company profile — mirrors
 *  `company_schema.COMPANY_OWNED_FIELDS` key-for-key (sync-tested). Values for
 *  these fields live on the company and are shared across assessments; used to
 *  badge shared fields in the intake form. */
export const COMPANY_OWNED_FIELDS: Record<string, string[]> = {
  company_profile: [
    'legal_name', 'aliases', 'business_description', 'industry',
    'sub_industry', 'company_size',
    'hq_city', 'hq_state', 'hq_postal_code', 'hq_country',
    'contacts',
  ],
  customer_identity: [
    'aws_segment', 'geographic_scope', 'regions', 'aws_relationship',
    'annual_aws_spend', 'aws_account_ids',
  ],
  business_context: [
    'top_priorities', 'pain_points', 'lost_money_time_customers',
    'competitive_ai_pressure', 'cx_falling_short', 'ex_inefficiencies',
    'first_workflow_to_automate', 'opportunity_theme', 'urgency_level',
    'urgency_driver', 'success_12mo',
  ],
  ai_landscape: [
    'production_ml_workloads', 'rpa_tools', 'existing_chatbots', 'genai_pilots',
    'competing_platforms', 'automation_candidates', 'ai_maturity',
    'prior_ai_outcomes',
  ],
  tech_stack: [
    'aws_infrastructure_summary',
    'aws_pattern', 'active_aws_services', 'bedrock_models', 'bedrock_quota',
    'iam_maturity', 'iac_cicd', 'observability', 'auth_idp', 'networking',
    'saas_systems', 'internal_systems', 'knowledge_bases', 'vector_store_rag',
    'mcp_servers',
  ],
  data_readiness: [
    'primary_locations', 'volume', 'data_catalog', 'data_quality', 'freshness',
    'languages', 'pii_sensitivity', 'biases_gaps',
  ],
  governance: [
    'compliance_frameworks', 'data_residency', 'ai_policy', 'review_process',
    'guardrails', 'hitl_requirements', 'audit_retention', 'prohibitions',
  ],
  organizational_readiness: [
    'team_composition', 'team_skills', 'training_certs', 'change_mgmt',
    'stakeholders',
  ],
}

/** The company-profile editor's section list: the company-owned AI-intake
 *  sections (each filtered to the fields in {@link COMPANY_OWNED_FIELDS} — the
 *  AI module's own fields inside mixed sections, e.g. the maturity scores and
 *  the sponsor/budget/timeline block, never render on the profile) followed by
 *  the three shared technical-baseline sections.
 *
 *  The intake SectionDefs deliberately stay defined in `intakeSchema.ts` (the
 *  backend sync test regex-parses that file); this helper only reuses them. */
export function companyProfileSections(): SectionDef[] {
  const owned = INTAKE_SECTIONS.filter((s) => s.key in COMPANY_OWNED_FIELDS).map((s) => ({
    ...s,
    fields: s.fields.filter((f) => COMPANY_OWNED_FIELDS[s.key]!.includes(f.key)),
  }))
  // company_profile is first in the intake schema's SECTIONS, so it leads here.
  return [...owned, ...BASELINE_SECTIONS]
}
